import { Card, Skeleton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { To } from "react-router";

import GaiaConfirmActionDialog from "~/modules/app/components/GaiaConfirmActionDialog";
import useDestructiveActionBlocker from "~/modules/app/hooks/useDestructiveActionBlocker";

import { isCarpoolableTravelGroup } from "../../util/isCarpoolableTravelGroup";
import { CarpoolHeader } from "./CarpoolHeader";
import { VisitTravelGroupsOverviewTravelGroupFragment } from "./fragments.generated";
import { ShiftRow } from "./ShiftRow";

export type TravelGroupCardProps = {
    travelGroup: VisitTravelGroupsOverviewTravelGroupFragment;
    onTravelGroupCancelClick: (shiftId: string) => void;
    travelGroupOpenHref: (travelGroupId: string) => To;
    onShiftInTravelGroupCancelClick: ({ shiftId, travelGroupId }: { shiftId: string; travelGroupId: string }) => void;

    isAddingToGroupId?: string;
    isCancelingShiftId?: string;
    isCancelingGroupId?: string;
};

export function TravelGroupCard({
    travelGroup,
    travelGroupOpenHref,
    onTravelGroupCancelClick,
    onShiftInTravelGroupCancelClick,
    isCancelingShiftId,
    isCancelingGroupId,
}: TravelGroupCardProps) {
    const { t } = useTranslation("plannerV2");
    const { id: travelGroupId, ownerBillingType, ownerPlannerShift, plannerShifts } = travelGroup;

    const isCarpool = plannerShifts.length > 1;
    const transportMethod = ownerBillingType === "none" ? "bike" : ownerBillingType;
    const headerLabel = isCarpoolableTravelGroup(travelGroup)
        ? t("visitDetail.carpool")
        : t(`visitDetail.travelMethod.${transportMethod}`);

    const passengerPlannerShifts = plannerShifts.filter(plannerShift => plannerShift.id !== ownerPlannerShift?.id);

    const cancelTravelGroupBlocker = useDestructiveActionBlocker(() => onTravelGroupCancelClick(travelGroupId), isCarpool);

    return (
        <>
            <GaiaConfirmActionDialog
                title={t("cancelCarpool.title")}
                description={t("cancelCarpool.description")}
                blocker={cancelTravelGroupBlocker}
            />
            <Card component="ul" sx={{ padding: 0, margin: 0 }}>
                <CarpoolHeader label={headerLabel} openHref={() => travelGroupOpenHref(travelGroupId)} />
                {ownerPlannerShift && (
                    <ShiftRow
                        key={ownerPlannerShift.id}
                        shift={ownerPlannerShift}
                        isOwner={true}
                        transportMethod={transportMethod}
                        onRemoveClick={cancelTravelGroupBlocker.onAction}
                        isBeingRemoved={isCancelingGroupId === travelGroupId}
                    />
                )}
                {passengerPlannerShifts.map(shift => (
                    <ShiftRow
                        key={shift.id}
                        isOwner={false}
                        shift={shift}
                        transportMethod="pickup"
                        onRemoveClick={() => onShiftInTravelGroupCancelClick({ shiftId: shift.id, travelGroupId })}
                        isBeingRemoved={isCancelingGroupId === travelGroupId || isCancelingShiftId === shift.id}
                    />
                ))}
            </Card>
        </>
    );
}

export function TravelGroupCardSkeleton() {
    return (
        <Card component="li" sx={{ padding: 0, margin: 0 }}>
            <Stack padding={2} gap={1.5}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Stack flexGrow={1}>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                    </Stack>
                    <Skeleton variant="circular" width={24} height={24} />
                </Stack>
            </Stack>
        </Card>
    );
}
